import React from 'react'
import { css } from '@emotion/react'
import map from 'lodash/map'

const placement = {
  horizontal: {
    alignItems: (anchor: HorizontalProps['anchor']) => {
      if (!anchor) {
        return undefined
      }

      if (/top/i.test(anchor)) {
        return 'start'
      }

      if (/bottom/i.test(anchor)) {
        return 'end'
      }

      if (/middle/i.test(anchor)) {
        return 'center'
      }

      return 'stretch'
    },
    justifyContent: (anchor: HorizontalProps['anchor']) => {
      if (!anchor) {
        return undefined
      }

      if (/stretch/i.test(anchor)) {
        return 'stretch'
      }

      if (/left/i.test(anchor)) {
        return 'start'
      }

      if (/right/i.test(anchor)) {
        return 'end'
      }

      if (/center/i.test(anchor)) {
        return 'center'
      }

      return undefined
    },
  },
  vertical: {
    alignItems: (anchor: VerticalProps['anchor']) => {
      if (!anchor) {
        return undefined
      }

      if (/left/i.test(anchor)) {
        return 'start'
      }

      if (/right/i.test(anchor)) {
        return 'end'
      }

      if (/center/i.test(anchor)) {
        return 'center'
      }

      return 'stretch'
    },
    justifyContent: (anchor: VerticalProps['anchor']) => {
      if (!anchor) {
        return undefined
      }

      if (/top/i.test(anchor)) {
        return 'start'
      }

      if (/bottom/i.test(anchor)) {
        return 'end'
      }

      if (/middle/i.test(anchor)) {
        return 'center'
      }

      return undefined
    },
  },
}

type Props = {
  className?: string
  gap: number
  style?: React.CSSProperties
  children: React.ReactNode
}

type HorizontalProps = Props & {
  direction: 'horizontal'
  anchor?:
    | 'stretch'
    | 'left'
    | 'right'
    | 'center'
    | 'topLeft'
    | 'topRight'
    | 'topCenter'
    | 'middleLeft'
    | 'middleRight'
    | 'middleCenter'
    | 'bottomLeft'
    | 'bottomRight'
    | 'bottomCenter'
}

type VerticalProps = Props & {
  direction: 'vertical'
  anchor?:
    | 'stretch'
    | 'top'
    | 'bottom'
    | 'middle'
    | 'topLeft'
    | 'topRight'
    | 'topCenter'
    | 'middleLeft'
    | 'middleRight'
    | 'middleCenter'
    | 'bottomLeft'
    | 'bottomRight'
    | 'bottomCenter'
}

export default function Spacer(props: HorizontalProps | VerticalProps) {
  if (!props.children) {
    return null
  }

  const createRenderItem = (prefix: string) => (
    child: React.ReactNode,
    index: number,
  ): React.ReactNode => {
    // Use `_.map` instead of `React.Children.map` to preserve the original indexes
    if (Array.isArray(child)) {
      return map(child, createRenderItem(index + '-'))
    }

    if (!React.isValidElement(child)) {
      return null
    }

    if (child.type === React.Fragment) {
      return map(child.props.children, createRenderItem(index + '-'))
    }

    if (child.type === Spacer.Expandable) {
      return child
    }

    return (
      <div
        key={prefix + index}
        css={css`
          :empty {
            display: none;
          }
        `}
        style={{
          width: props.anchor === 'stretch' ? '100%' : undefined,
        }}
      >
        {child}
      </div>
    )
  }

  return (
    <div
      className={props.className}
      css={[
        css`
          display: flex;
        `,
        props.direction === 'horizontal'
          ? css`
              flex-direction: row;
            `
          : css`
              flex-direction: column;
              height: 100%;
            `,
      ]}
      style={{
        gap: props.gap,
        alignItems:
          props.direction === 'horizontal'
            ? placement.horizontal.alignItems(props.anchor)
            : placement.vertical.alignItems(props.anchor),
        justifyContent:
          props.direction === 'horizontal'
            ? placement.horizontal.justifyContent(props.anchor)
            : placement.vertical.justifyContent(props.anchor),
        ...props.style,
      }}
    >
      {createRenderItem('')(props.children, 0)}
    </div>
  )
}

Spacer.Expandable = function SpacerExpandable(props: { children?: React.ReactNode }) {
  return <div style={{ flex: '1 1 auto' }}>{props.children}</div>
}
